import { createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { getCareerAdmin, updateCareer } from "@/lib/api/careers.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { BlockDocEditor } from "@/components/admin/doc/BlockDocEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { resolveEditorDocForLoad } from "@/lib/doc/doc-convert";
import type { EditorDoc } from "@/lib/doc/doc-types";

export const Route = createFileRoute("/admin/careers/$id")({
  loader: ({ params }) => getCareerAdmin({ data: { id: params.id } }),
  component: EditCareerPage,
});

function EditCareerPage() {
  const job = Route.useLoaderData();
  if (!job) throw notFound();
  const [title, setTitle] = useState(job.title);
  const [status, setStatus] = useState(job.status);
  const [location, setLocation] = useState(job.location ?? "");
  const [employmentType, setEmploymentType] = useState(job.employmentType ?? "");
  const [department, setDepartment] = useState(job.department ?? "");
  const [applyEmail, setApplyEmail] = useState(job.applyEmail ?? "");
  const [excerpt, setExcerpt] = useState(job.excerpt ?? "");
  const [editorDoc, setEditorDoc] = useState<EditorDoc>(() =>
    resolveEditorDocForLoad({
      editorDoc: job.editorDoc,
      craftState: job.craftState,
      contentHtml: job.contentHtml,
    }),
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await updateCareer({
        data: {
          id: job.id,
          title,
          status,
          location,
          employmentType,
          department: department || undefined,
          applyEmail,
          excerpt,
          editorDoc,
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Edit job" />
      <ContentAdminTabs
        settingsLabel="Job settings"
        settingsForm={
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Location</Label><Input value={location} onChange={(e) => setLocation(e.target.value)} /></div>
            <div className="space-y-2"><Label>Employment type</Label><Input value={employmentType} onChange={(e) => setEmploymentType(e.target.value)} /></div>
            <div className="space-y-2"><Label>Department</Label><Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Tax, Audit, Advisory" /></div>
            <div className="space-y-2"><Label>Apply email</Label><Input value={applyEmail} onChange={(e) => setApplyEmail(e.target.value)} type="email" /></div>
            <div className="space-y-2"><Label>Excerpt</Label><Input value={excerpt} onChange={(e) => setExcerpt(e.target.value)} /></div>
          </div>
        }
        content={
          <BlockDocEditor
            editorKey={job.id}
            initialDoc={editorDoc}
            onChange={setEditorDoc}
            className="w-full"
          />
        }
      />
      <AdminFormFooter cancelTo="/admin/careers" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
